import { Box, ChipProps, GridLegacy as Grid, Typography } from '@mui/material'
import { C1Chip } from 'c1ui/components/C1Chip'
import { SpacingVariants, TableGrid } from 'components/core/table-grid'
import { UserDirectoriesProps, UserDirectoryStatus, UserEmployeeId, UserJob } from 'components/core/user'
import { VariableText } from 'components/core/variable-text'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { ProfileList } from 'components/pages/app/users/details'
import { UserAttributeDisplay } from 'components/pages/user/details'
import { userStatusToIntl, userStatusToIdentifier } from 'components/pages/user/type'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user/index'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const UserDirectories: FC<UserDirectoriesProps> = ({ user }) => user && <Grid container item direction="column">
    {user.directories?.map((directory) => (
        <Grid item key={directory.id}>
            <Box sx={{ display: 'inline-block', width: 'auto' }}>
                <AppDisplayName margin={false} app={directory} />
            </Box>
        </Grid>
    ))}
</Grid>

export const UserEmploymentStatus: FC<UserDirectoriesProps> = ({ user }) =>
    user && <Box display="flex" width="100%">
        <UserAttributeDisplay
            user={user}
            sources={user.employmentStatusSources}
            value={user.employmentStatus}
        />
    </Box>

export const UserEmploymentType: FC<UserDirectoriesProps> = ({ user }) =>
    user && <Box display="flex" width="100%">
        <UserAttributeDisplay
            user={user}
            sources={user.employmentTypeSources}
            value={user.employmentType}
        />
    </Box>

export const UserDetail = ({ user, forceLinear = false }: { user: UserValue, forceLinear?: boolean }): React.ReactElement => {
    const intl = useIntl()
    const userName = user?.displayName || ''
    const extraEmails = user?.emails.filter((e) => e !== user?.email)
    const extraUsernames = user?.usernames.filter((e) => e !== user?.username)
    const identifierProps = userStatusToIdentifier(user?.status, intl)

    const userDetailsChipProps: ChipProps = {
        color: identifierProps.color,
        label: user ? userStatusToIntl(user.status, intl) : '',
    }

    return <>
        <TableGrid
            title={<FormattedMessage id="user_details_header" defaultMessage="User details" />}
            chip={userDetailsChipProps}
            spacingVariant={SpacingVariants.compact}
        >
            <Grid item container direction="row" spacing={4}>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_name" defaultMessage="Name" />
                    </Typography>
                    <Typography noWrap>{userName}</Typography>
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_managers" defaultMessage="Managers" />
                    </Typography>
                    <VariableText
                        component="div"
                        variableText={user?.managers?.map((manager) => (
                            <UserDisplayName
                                key={manager.id}
                                user={manager}
                                includeDetails={false}
                                showTooltip
                            />
                        ))}
                    />
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_role" defaultMessage="Role" />
                    </Typography>
                    {user && (
                        <VariableText
                            noWrap
                            variableText={user.roles?.map((r) => r.displayName).join(', ')}
                        />
                    )}
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_email" defaultMessage="Email" />
                    </Typography>
                    <Grid container>
                        <Typography noWrap>{user?.email}</Typography>
                        {extraEmails && extraEmails.length > 0 &&
                            <C1Chip label={intl.formatMessage({
                                id: 'user_details_primary_email',
                                defaultMessage: 'Primary',
                            })}
                            />
                        }
                    </Grid>
                    {extraEmails && extraEmails.map((e) => {
                        return <div key={e}><Typography noWrap>{e}</Typography></div>
                    })}
                </Grid>

                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_username" defaultMessage="Username" />
                    </Typography>
                    <Grid container>
                        <VariableText
                            noWrap
                            variableText={user?.username}
                        />
                        {extraUsernames && extraUsernames.length > 0 &&
                            <C1Chip label={intl.formatMessage({
                                id: 'user_details_primary_username',
                                defaultMessage: 'Primary',
                            })}
                            />
                        }
                    </Grid>
                    {extraUsernames && extraUsernames.map((e) => {
                        return <div key={e}><Typography noWrap>{e}</Typography></div>
                    })}
                </Grid>

                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_employee_id" defaultMessage="Employee ID" />
                    </Typography>
                    <UserEmployeeId user={user} intl={intl} />
                </Grid>

                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_directories" defaultMessage="Directories" />
                    </Typography>
                    <Grid container item direction="column">
                        <UserDirectories user={user} intl={intl} />
                    </Grid>
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_directory_status" defaultMessage="Directory status" />
                    </Typography>
                    <UserDirectoryStatus user={user} intl={intl} />
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_job_title" defaultMessage="Job title" />
                    </Typography>
                    <UserJob user={user} intl={intl} />
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_department" defaultMessage="Department" />
                    </Typography>
                    <VariableText
                        noWrap
                        variableText={user.department}
                    />
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage
                            id="user_details_employment_status"
                            defaultMessage="Employment status"
                        />
                    </Typography>
                    <UserEmploymentStatus user={user} intl={intl} />
                </Grid>
                <Grid item md={12} lg={forceLinear ? 12 : 6} zeroMinWidth>
                    <Typography variant="h6" pb={1} noWrap>
                        <FormattedMessage id="user_details_employment_type" defaultMessage="Employment type" />
                    </Typography>
                    <UserEmploymentType user={user} intl={intl} />
                </Grid>
            </Grid>
        </TableGrid>
        <TableGrid
            title={<FormattedMessage id="user_details_profile_attributes" defaultMessage="Profile attributes" />}
            spacingVariant={SpacingVariants.compact}
        >
            <Box px={2} pt={1} flexGrow={1}>
                <ProfileList profile={user.profile} userId={user.id} showProfileTypeSources />
            </Box>
        </TableGrid>
    </>
}
